import { useCallback, useEffect, useState } from 'react';

/**
 * Theme preference.
 *
 * Without a deliberate choice the page follows the system setting (light is the
 * design's default where the system states none). A choice made with the toggle
 * is stored and wins from then on. Storage can be unavailable (private mode,
 * blocked site data); every access is guarded and the page still renders.
 */

export type Theme = 'light' | 'dark';
export const THEME_KEY = 'hugarheim-theme';

export function storedTheme(): Theme | null {
  try {
    const v = window.localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
}

function systemTheme(): Theme {
  try {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

/** Applies the stored choice before first paint; no attribute means "follow the system". */
export function applyInitialTheme(): void {
  const t = storedTheme();
  if (t) document.documentElement.dataset.theme = t;
  else delete document.documentElement.dataset.theme;
}

export function useTheme() {
  const [choice, setChoice] = useState<Theme | null>(() => storedTheme());
  const [system, setSystem] = useState<Theme>(() => systemTheme());

  useEffect(() => {
    let mq: MediaQueryList | undefined;
    try {
      mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    } catch {
      mq = undefined;
    }
    if (!mq) return;
    const onChange = () => setSystem(mq!.matches ? 'dark' : 'light');
    mq.addEventListener?.('change', onChange);
    return () => mq!.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    if (choice) document.documentElement.dataset.theme = choice;
    else delete document.documentElement.dataset.theme;
  }, [choice]);

  const effective: Theme = choice ?? system;

  const toggle = useCallback(() => {
    const next: Theme = effective === 'dark' ? 'light' : 'dark';
    setChoice(next);
    try {
      window.localStorage.setItem(THEME_KEY, next);
    } catch {
      /* the choice still applies for this visit */
    }
  }, [effective]);

  const followSystem = useCallback(() => {
    setChoice(null);
    try {
      window.localStorage.removeItem(THEME_KEY);
    } catch {
      /* nothing stored, nothing to remove */
    }
  }, []);

  return { theme: effective, deliberate: choice !== null, toggle, followSystem };
}
